import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, platformLabel } from "../lib/api";
import ProductCard from "../components/ProductCard";
import PageHero from "../components/PageHero";
import Loading from "../components/Loading";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";

const FILTERS = {
  category: [
    { v: "", l: "All" },
    { v: "consoles", l: "Consoles" },
    { v: "games", l: "Games" },
    { v: "accessories", l: "Accessories" },
    { v: "controllers", l: "Controllers" },
    { v: "digital", l: "Digital" },
  ],
  platform: [
    { v: "", l: "All" },
    { v: "ps5", l: "PS5" },
    { v: "ps4", l: "PS4" },
    { v: "switch 2", l: "Switch 2" },
    { v: "switch 1", l: "Switch 1" },
    { v: "xbox", l: "Xbox" },
  ],
  condition: [
    { v: "", l: "All" },
    { v: "new", l: "New" },
    { v: "used", l: "Used" },
    { v: "upcoming", l: "Pre-Book" },
  ],
};

export default function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/products/search", {
          params: {
            q,
            category: params.get("category") || undefined,
            condition: params.get("condition") || undefined,
            platform: params.get("platform") || undefined,
            limit: 100,
          },
        });
        setResults(r.data);
      } catch {
        setResults([]);
      }
      setLoading(false);
    })();
  }, [params, q]);

  const setFilter = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np);
  };

  const clearAll = () => {
    const np = new URLSearchParams();
    if (q) np.set("q", q);
    setParams(np);
  };

  const activeCount = ["category", "platform", "condition"].filter((k) => params.get(k)).length;

  return (
    <div>
      <Helmet>
        <title>{q ? `"${q}" — Search GamesTrade` : "Search — GamesTrade"}</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <PageHero
        eyebrow="Search Results"
        title={
          q ? (
            <>For "<span className="text-gold">{q}</span>"</>
          ) : (
            <>Find your next <span className="text-gold">drop</span></>
          )
        }
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Search" }]}
        size="compact"
      />

      <div className="site-container py-10">

        {/* Top bar: filter toggle + active pills + count */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            onClick={() => setPanelOpen((v) => !v)}
            className={`flex items-center gap-2 border rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider transition ${
              panelOpen
                ? "bg-gold text-navy-900 border-gold"
                : "bg-navy-700 border-gold/20 hover:border-gold/50"
            }`}
            data-testid="search-filter-toggle"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeCount > 0 && (
              <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${panelOpen ? "bg-navy-900 text-gold" : "bg-gold text-navy-900"}`}>
                {activeCount}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${panelOpen ? "rotate-180" : ""}`} />
          </button>

          {/* Active filter pills */}
          {["category", "platform", "condition"].map((k) => {
            const v = params.get(k);
            if (!v) return null;
            const display = k === "platform" ? platformLabel(v) : v.charAt(0).toUpperCase() + v.slice(1);
            return (
              <button
                key={k}
                onClick={() => setFilter(k, "")}
                className="flex items-center gap-1.5 bg-gold/10 border border-gold/30 text-gold text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1.5 hover:bg-gold/20 transition"
              >
                {display} <X className="w-3 h-3" />
              </button>
            );
          })}

          <div className="ml-auto text-xs text-slate-400 uppercase tracking-wider">{results.length} matches</div>
        </div>

        {/* Inline animated filter panel */}
        <div
          ref={panelRef}
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: panelOpen ? "500px" : "0px", opacity: panelOpen ? 1 : 0 }}
        >
          <div className="bg-navy-700/60 border border-gold/15 rounded-2xl p-5 mb-6 backdrop-blur">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              {Object.entries(FILTERS).map(([key, opts]) => (
                <div key={key} data-testid={`filter-${key}`}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold/70 mb-2">{key}</div>
                  <div className="flex flex-col gap-0.5">
                    {opts.map((o) => {
                      const active = (params.get(key) || "") === o.v;
                      return (
                        <button
                          key={o.v}
                          onClick={() => setFilter(key, o.v)}
                          data-testid={`search-filter-${key}-${o.v || "all"}`}
                          className={`text-left text-sm px-3 py-1.5 rounded-md transition ${
                            active ? "bg-gold text-navy-900 font-bold" : "text-slate-300 hover:bg-navy-600"
                          }`}
                        >
                          {o.l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {activeCount > 0 && (
              <div className="mt-4 pt-4 border-t border-gold/10 flex items-center justify-between">
                <span className="text-xs text-slate-400">{activeCount} filter{activeCount > 1 ? "s" : ""} active</span>
                <button
                  onClick={clearAll}
                  className="text-xs uppercase tracking-wider text-rose-400 hover:text-rose-300 font-bold"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <Loading variant="block" label="Searching" />
        ) : results.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            No matches. Try a different search or remove filters.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {results.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
